import { useEffect, useId, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useSidebarToggle } from "context/ToggleSideBarContext";
import Icon, { IconName } from "components/Icon";
import { useBuildUrlWithContext } from "hooks/useBuildUrlWithContext";

export type PermissionLink = {
  permission: string | string[];
  to: string;
  title: string;
  icon?: string;
};

export type MenuItemProps = {
  permissions?: string[];
  permissionsToMatch?: PermissionLink | PermissionLink[];
  iconClassName: IconName;
  navTitle: string;
  basePermission?: string;
  isOpen?: boolean;
  onToggle?: () => void;
  navigateTo?: string;
  iconSize?: number;
  as?: "button" | "link";
  onClick?: () => void;
};

const matchedPermission = (
  permissions: string[],
  permissionToMatch: PermissionLink[],
) =>
  permissionToMatch.filter((link) => {
    if (Array.isArray(link.permission)) {
      return link.permission.some((p) => permissions.includes(p));
    }
    return permissions.includes(link.permission);
  });

export default function MenuItem({
  permissions,
  permissionsToMatch,
  iconClassName,
  navTitle,
  basePermission,
  isOpen: controlledIsOpen,
  onToggle,
  navigateTo,
  iconSize = 20,
  as,
  onClick,
}: MenuItemProps) {
  const { isSideBarOpen, toggleSidebar } = useSidebarToggle();
  const buildUrlWithContext = useBuildUrlWithContext();
  const location = useLocation();
  const submenuId = useId();

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  const [internalOpen, setInternalOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("");

  const open = controlledIsOpen !== undefined ? controlledIsOpen : internalOpen;

  /* ---------------- Permission logic (runs unconditionally) ---------------- */

  const normalizedPermissions = permissionsToMatch
    ? Array.isArray(permissionsToMatch)
      ? permissionsToMatch
      : [permissionsToMatch]
    : [];

  let menuItems = permissions
    ? matchedPermission(permissions, normalizedPermissions)
    : [];

  if (basePermission && permissions?.includes(basePermission)) {
    menuItems = normalizedPermissions;
  }

  const currentPath = location.pathname;
  const matchedLink = menuItems.find((link) => {
    const linkPath = new URL(link.to, window.location.origin).pathname;
    const regex = new RegExp(`^${linkPath}(/|$)`);
    return regex.test(currentPath);
  });

  /* ---------------- ALL hooks must run before any return ---------------- */

  useEffect(() => {
    if (as === "button") return;
    if (matchedLink) {
      setActiveLink(matchedLink.to);
      if (controlledIsOpen === undefined) {
        setInternalOpen(true);
      }
    } else {
      setActiveLink("");
      if (controlledIsOpen === undefined) {
        setInternalOpen(false);
      }
    }
  }, [matchedLink, controlledIsOpen, as]);

  useEffect(() => {
    if (as === "button") return;
    if (!isSideBarOpen && controlledIsOpen === undefined) {
      setInternalOpen(false);
    }
  }, [isSideBarOpen, controlledIsOpen, as]);

  /* ---------------- Standalone button mode ---------------- */

  if (as === "button") {
    return (
      <li className="menu-item">
        <button type="button" className="item nav-link" onClick={onClick}>
          <span className="accordion__title__container">
            <span className="accordion__title">
              <Icon
                name={iconClassName}
                size={iconSize}
                title={navTitle}
              />
              <span className="title">{navTitle}</span>
            </span>
          </span>
        </button>
      </li>
    );
  }

  if (!menuItems.length) return null;

  /* ---------------- Direct Navigation Mode ---------------- */

  if (navigateTo) {
    const urlWithContext = buildUrlWithContext(navigateTo);

    return (
      <li className="menu-item">
        <NavLink
          to={urlWithContext}
          className={({ isActive }) =>
            `item nav-link${isActive ? " active" : ""}`
          }
          onClick={(e) => {
            if (!e.ctrlKey && !e.metaKey && e.button === 0) {
              e.preventDefault();
              window.history.pushState({}, "", navigateTo);
              window.dispatchEvent(new PopStateEvent("popstate"));
              onToggle?.();
            }
          }}
        >
          <span className="accordion__title__container">
            <span className="accordion__title">
              <Icon
                name={iconClassName}
                size={iconSize}
                title={navTitle}
                onClick={!isSideBarOpen ? toggleSidebar : undefined}
              />
              <span className="title">{navTitle}</span>
            </span>
          </span>
        </NavLink>
      </li>
    );
  }

  /* ---------------- Keyboard Handlers ---------------- */

  const toggleSubmenu = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalOpen((prev) => !prev);
    }
  };

  const handleTriggerKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        toggleSubmenu();
        break;

      case "ArrowDown":
        event.preventDefault();
        if (!open) {
          if (onToggle) {
            onToggle();
          } else {
            setInternalOpen(true);
          }
        }
        requestAnimationFrame(() => {
          itemRefs.current[0]?.focus();
        });
        break;

      case "Escape":
        event.preventDefault();
        if (onToggle && open) {
          onToggle();
        } else if (controlledIsOpen === undefined) {
          setInternalOpen(false);
        }
        break;
    }
  };

  const handleSubmenuKeyDown = (
    event: React.KeyboardEvent<HTMLAnchorElement>,
    index: number,
  ) => {
    const lastIndex = menuItems.length - 1;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        itemRefs.current[index + 1]?.focus() ?? itemRefs.current[0]?.focus();
        break;

      case "ArrowUp":
        event.preventDefault();
        itemRefs.current[index - 1]?.focus() ??
          itemRefs.current[lastIndex]?.focus();
        break;

      case "Home":
        event.preventDefault();
        itemRefs.current[0]?.focus();
        break;

      case "End":
        event.preventDefault();
        itemRefs.current[lastIndex]?.focus();
        break;

      case "Escape":
      case "ArrowLeft":
        event.preventDefault();
        if (onToggle && open) {
          onToggle();
        } else if (controlledIsOpen === undefined) {
          setInternalOpen(false);
        }
        triggerRef.current?.focus();
        break;
    }
  };

  /* ---------------- Misc ---------------- */

  const handleResetSessionStorage = (
    e: React.MouseEvent<HTMLAnchorElement>,
    link: PermissionLink,
  ) => {
    if (e.ctrlKey || e.metaKey || e.button === 1) return;

    if (link.title !== "Customer File") {
      [
        "basicSearchOptions",
        "advanceSearchOptions",
        "selectedMasterType",
      ].forEach((k) => sessionStorage.removeItem(k));
    }
  };

  /* ---------------- JSX ---------------- */

  return (
    <li className="menu-item">
      <button
        ref={triggerRef}
        type="button"
        className="item accordion-trigger"
        aria-expanded={open}
        aria-controls={submenuId}
        onClick={toggleSubmenu}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className="accordion__title__container">
          <span className="accordion__title">
            <Icon
              name={iconClassName}
              size={iconSize}
              title={navTitle}
              onClick={!isSideBarOpen ? toggleSidebar : undefined}
              tabIndex={-1}
            />
            <span className="title">{navTitle}</span>
          </span>

          <span className="accordion__arrow__icon" aria-hidden>
            <i className="icon-arrow-down icon" />
          </span>
        </span>
      </button>

      <ul
        id={submenuId}
        role="menu"
        hidden={!open}
        className={`sublinks__container ${open && isSideBarOpen ? "sublinks__container__open" : ""
          }`}
      >
        {menuItems.map((link, index) => {
          const urlWithContext = buildUrlWithContext(link.to);

          return (
            <li key={link.to} role="none">
              <NavLink
                ref={(el) => (itemRefs.current[index] = el)}
                to={urlWithContext}
                role="menuitem"
                tabIndex={open ? 0 : -1}
                className={`nav-link sub-item ${activeLink === link.to ? "active" : ""
                  }`}
                onClick={(e) => {
                  if (!e.ctrlKey && !e.metaKey && e.button === 0) {
                    e.preventDefault();
                    window.history.pushState({}, "", link.to);
                    window.dispatchEvent(new PopStateEvent("popstate"));
                  }
                  handleResetSessionStorage(e, link);
                }}
                onKeyDown={(e) => handleSubmenuKeyDown(e, index)}
                onFocus={() => setActiveLink(link.to)}
              >
                <span className="title">{link.title}</span>
                {link.icon && <i className={link.icon} aria-hidden />}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </li>
  );
}